import { useState, useEffect } from "react";
import Button from "@web-ui/components/Button";
import Logo from "@web-ui/components/Logo";
import User from "./User";
import type { UserMenuItem } from "@web-ui/types/navigation";

/*
 * Template Header with its mock widgets taken out: the search panel, the
 * "Create" button, notifications, messages and the new product publish row
 * read the kit's mock data. The app passes its own controls in `actions`; the
 * user menu takes the signed in person and their menu items.
 */
type HeaderProps = {
    className?: string;
    title?: string;
    /** One quiet line under the title that says what the page is for; stays in view while the page scrolls. */
    subtitle?: string;
    brand?: React.ReactNode;
    actions?: React.ReactNode;
    user?: {
        name: string;
        detail?: string;
        avatarSrc?: string | null;
        items: UserMenuItem[];
        onLogout: () => void;
        logoutLabel?: string;
    };
    hideSidebar?: boolean;
    onToggleSidebar?: () => void;
    toggleSidebarLabel?: string;
};

const Header = ({
    className,
    title,
    subtitle,
    brand,
    actions,
    user,
    hideSidebar,
    onToggleSidebar,
    toggleSidebarLabel = "Menu",
}: HeaderProps) => {
    const [hasOverflowHidden, setHasOverflowHidden] = useState(false);

    useEffect(() => {
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.attributeName === "style") {
                    const htmlElement = document.documentElement;
                    const isOverflowHidden =
                        window.getComputedStyle(htmlElement).overflow ===
                        "hidden";
                    setHasOverflowHidden(isOverflowHidden);
                }
            });
        });

        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ["style"],
        });

        return () => observer.disconnect();
    }, []);

    return (
        <header
            className={`fixed top-0 end-0 z-20 bg-b-surface1 max-lg:!end-0 ${
                hasOverflowHidden
                    ? "end-[calc(0px+var(--scrollbar-width))]"
                    : ""
            } ${
                hideSidebar
                    ? "start-0"
                    : "start-85 max-4xl:start-70 max-3xl:start-60 max-xl:start-0"
            } ${className || ""}`}
        >
            <div
                className={`flex items-center max-md:h-18 ${subtitle ? "h-26" : "h-22"} ${
                    hideSidebar ? "center max-w-full" : "center-with-sidebar"
                }`}
            >
                <div
                    className={`me-3 gap-3 ${
                        hideSidebar ? "flex" : "hidden max-xl:flex"
                    }`}
                >
                    <Logo>{brand}</Logo>
                    <Button
                        className="flex-col gap-[4.5px] shrink-0 before:w-4.5 before:h-[1.5px] before:rounded-full before:bg-t-secondary before:transition-colors after:w-4.5 after:h-[1.5px] after:rounded-full after:bg-t-secondary after:transition-colors hover:before:bg-t-primary hover:after:bg-t-primary"
                        onClick={onToggleSidebar}
                        aria-label={toggleSidebarLabel}
                        isCircle
                        isWhite
                    />
                </div>
                {/* The title stays on phones, compact (owner 2026-10-13): it is the page's only heading there; the
                    subtitle yields to the controls below md. */}
                {title && (
                    <div className="me-auto min-w-0">
                        <h1 className="truncate text-page-title max-md:text-sub-title-1">{title}</h1>
                        {subtitle && (
                            <p className="mt-1 truncate text-body-2 text-t-secondary max-md:hidden" title={subtitle}>
                                {subtitle}
                            </p>
                        )}
                    </div>
                )}
                <div
                    className={`flex shrink-0 items-center gap-3 ${
                        hideSidebar ? "grow max-lg:grow-0 max-lg:ms-auto" : ""
                    }`}
                >
                    {actions}
                    {user && <User {...user} />}
                </div>
            </div>
        </header>
    );
};

export default Header;
